/**
 * Checkout Wizard Mockup V2
 * Interactive prototype with Modern Design & Hybrid Features
 */

import React, { useState } from 'react';
import './checkout-wizard-mockup-v2.scss';
import {
  mockStoreDetails,
  mockAddresses,
  mockTimeSlots,
  mockPriceList,
  mockPickupPriceList,
  mockCartInfo,
  mockCoupons,
  mockEbtPaidList,
  mockEbtPaidListEmpty,
  mockUser,
  mockDoorDashOptions,
  mockTipOptions,
  mockContactTypes,
  mockPaymentMethods,
  wizardSteps,
  initialFormState,
} from './mockData';

const CheckoutWizardMockup2 = () => {
  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const [formState, setFormState] = useState(initialFormState);
  const [showEbtPayments, setShowEbtPayments] = useState(false);
  const [selectedCoupons, setSelectedCoupons] = useState(['SAVE10']);

  // Derived state
  const isDelivery = formState.deliveryMethod === 'delivery';
  const isDoorDash = isDelivery && mockStoreDetails.third_party_delivery_option === 'door_dash';
  const priceList = isDelivery ? mockPriceList : mockPickupPriceList;
  const paymentMethods = isDelivery 
    ? mockPaymentMethods.grocery.delivery 
    : mockPaymentMethods.grocery.pickup;

  // Handlers
  const updateForm = (field, value) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  };

  const handleStepClick = (stepId) => {
    setCurrentStep(stepId);
    window.scrollTo(0, 0);
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleCouponToggle = (couponValue) => {
    setSelectedCoupons(prev => 
      prev.includes(couponValue)
        ? prev.filter(c => c !== couponValue)
        : [...prev, couponValue]
    );
  };

  const getStepStatus = (stepId) => {
    if (stepId === currentStep) return 'active';
    if (stepId < currentStep) return 'completed';
    return 'disabled';
  };

  // Progress Step Component
  const ProgressSteps = () => (
    <nav className="wizard-progress">
      {wizardSteps.map((step, index) => {
        const status = getStepStatus(step.id);
        const isActive = status === 'active';
        const isCompleted = status === 'completed';
        
        return (
          <React.Fragment key={step.id}>
            <div
              className={`wizard-progress__step wizard-progress__step--${status}`}
              onClick={() => (isCompleted || isActive) && handleStepClick(step.id)}
            >
              <div className="wizard-progress__step-icon">
                {step.icon === 'fa-truck' && '🚚'}
                {step.icon === 'fa-location-dot' && '📍'}
                {step.icon === 'fa-credit-card' && '💳'}
              </div>
              <span>{step.title}</span>
            </div>
            {index < wizardSteps.length - 1 && (
              <div className={`wizard-progress__separator ${isCompleted ? 'wizard-progress__separator--filled' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );

  return (
    <div className="checkout-wizard-v2">
      <header className="checkout-wizard-v2__header">
        <div className="checkout-wizard-v2__container header-content">
          <h1>Checkout</h1>
          <ProgressSteps />
        </div>
      </header>

      <div className="checkout-wizard-v2__container checkout-wizard-v2__grid">
        {/* Main Content Area */}
        <main className="checkout-wizard-v2__main">
          {currentStep === 1 && (
            <Step1DeliveryScheduling
              formState={formState}
              updateForm={updateForm}
              mockStoreDetails={mockStoreDetails}
              mockTimeSlots={mockTimeSlots}
              mockDoorDashOptions={mockDoorDashOptions}
              mockTipOptions={mockTipOptions}
              mockCartInfo={mockCartInfo}
              isDelivery={isDelivery}
              isDoorDash={isDoorDash}
            />
          )}

          {currentStep === 2 && (
            <Step2ContactAddress
              formState={formState}
              updateForm={updateForm}
              mockAddresses={mockAddresses}
              mockContactTypes={mockContactTypes}
              mockStoreDetails={mockStoreDetails}
              mockUser={mockUser}
              isDelivery={isDelivery}
            />
          )}

          {currentStep === 3 && (
            <Step3ReviewPayment
              formState={formState}
              updateForm={updateForm}
              mockStoreDetails={mockStoreDetails}
              mockAddresses={mockAddresses}
              mockCartInfo={mockCartInfo}
              paymentMethods={paymentMethods}
              showEbtPayments={showEbtPayments}
              mockEbtPaidList={showEbtPayments ? mockEbtPaidList : mockEbtPaidListEmpty}
              grandTotal={priceList.find(p => p.key_name === 'grand_total')?.amount}
            />
          )}

          {/* Navigation Buttons */}
          <div className="nav-buttons">
            <button
              type="button"
              className="btn-back"
              onClick={handleBack}
              style={{ visibility: currentStep === 1 ? 'hidden' : 'visible' }}
            >
              Back
            </button>
            
            {currentStep < 3 ? (
              <button
                type="button"
                className="btn-next"
                onClick={handleNext}
              >
                Next
              </button>
            ) : (
                <button type="button" className="btn-next btn-place-order" style={{ width: 'auto' }}>
                  🔒 Place Order • ${priceList.find(p => p.key_name === 'grand_total')?.amount}
                </button>
            )}
          </div>
        </main>

        {/* Sidebar */}
        <aside className="checkout-wizard-v2__sidebar">
          <OrderSummary
            priceList={priceList}
            mockStoreDetails={mockStoreDetails}
            mockCartInfo={mockCartInfo}
            mockCoupons={mockCoupons}
            selectedCoupons={selectedCoupons}
            onCouponToggle={handleCouponToggle}
            isDelivery={isDelivery}
          />
        </aside>
      </div>
    </div>
  );
};

// --- Sub-components ---

const Step1DeliveryScheduling = ({
  formState,
  updateForm,
  mockStoreDetails,
  mockTimeSlots,
  mockDoorDashOptions,
  mockTipOptions,
  mockCartInfo,
  isDelivery,
  isDoorDash,
}) => {
  return (
    <div className="wizard-card">
      <h2>Delivery Method</h2>
      <p className="card-description">Choose how you would like to receive your order.</p>
      
      <div className="option-grid">
        <div 
          className={`option-card ${!isDelivery ? 'option-card--selected' : ''}`}
          onClick={() => updateForm('deliveryMethod', 'store_pickup')}
        >
          <div className="check-mark-circle">✓</div>
          <div className="card-icon">🏪</div>
          <div className="card-title">Store Pickup</div>
          <div className="card-subtitle">Pick up at store</div>
        </div>

        <div 
          className={`option-card ${isDelivery ? 'option-card--selected' : ''}`}
          onClick={() => updateForm('deliveryMethod', 'delivery')}
        >
          <div className="check-mark-circle">✓</div>
          <div className="card-icon">🚚</div>
          <div className="card-title">{mockStoreDetails.delivery_method_lable}</div>
          <div className="card-subtitle">
            {isDoorDash ? 'Powered by DoorDash' : 'Local Delivery'}
          </div>
        </div>
      </div>

      {isDelivery && isDoorDash && (
        <div style={{ marginTop: '2rem' }}>
           <h3>Delivery Preferences</h3>
           
           <div className="option-grid" style={{ marginTop: '1rem', gridTemplateColumns: 'repeat(2, 1fr)' }}>
             {mockDoorDashOptions.map(opt => (
               <div
                 key={opt.value}
                 className={`option-card ${formState.doorDashContactless === opt.value ? 'option-card--selected' : ''}`}
                 onClick={() => updateForm('doorDashContactless', opt.value)}
                 style={{ padding: '1rem' }}
               >
                 <div className="check-mark-circle" style={{ width: '20px', height: '20px', fontSize: '12px', top: '0.5rem', right: '0.5rem' }}>✓</div>
                 <div className="card-title" style={{ fontSize: '0.95rem' }}>{opt.label}</div>
               </div>
             ))}
           </div>
           
           <div className="form-group">
            <label>Instructions for Driver</label>
            <textarea 
              placeholder="e.g. Gate code is 1234"
              value={formState.dropOffInstructions}
              onChange={(e) => updateForm('dropOffInstructions', e.target.value)}
              rows={3}
            />
           </div>

           {mockStoreDetails.allow_tip_amount === "1" && (
             <div className="form-group">
               <label>Driver Tip</label>
               <div className="option-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', marginBottom: 0 }}>
                 {mockTipOptions.map(tip => (
                   <div
                    key={tip.label}
                    className={`option-card option-card--compact ${formState.tipAmount === tip.value ? 'option-card--selected' : ''}`}
                    onClick={() => updateForm('tipAmount', tip.value)}
                   >
                     <div className="check-mark-circle">✓</div>
                     <div className="card-title">{tip.label}</div>
                   </div>
                 ))}
               </div>
             </div>
           )}

           <div 
             className={`toggle-card ${formState.doorDashConfirmed ? 'toggle-card--selected' : ''}`}
             onClick={() => updateForm('doorDashConfirmed', !formState.doorDashConfirmed)}
           >
              <div className="toggle-card__icon">📍</div>
              <div className="toggle-card__content">
                  <div className="toggle-card__title">Confirm Delivery Location</div>
                  <div className="toggle-card__subtitle">I confirm DoorDash delivers to my address</div>
              </div>
              <div className="toggle-card__toggle">
                 <div className={`switch`}></div>
              </div>
           </div>
        </div>
      )}

      <h2 style={{ marginTop: '2rem' }}>Select Time</h2>
      <p className="card-description">Select a time for {isDelivery ? 'delivery' : 'pickup'}.</p>
      
      {/* Changed to Option Grid for Larger, Detailed Cards - Using timeslot-card style to match 1st */}
      <div className="option-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
        {mockTimeSlots.map(slot => (
           <div
             key={slot.title}
             className={`timeslot-card ${formState.selectedTimeSlot?.title === slot.title ? 'timeslot-card--selected' : ''}`}
             onClick={() => updateForm('selectedTimeSlot', slot)}
           >
             <div className="check-mark-circle" style={{ width: '20px', height: '20px', fontSize: '12px', top: '0.5rem', right: '0.5rem' }}>✓</div>
             <div className="timeslot-card__date">{slot.label}</div>
             <div className="timeslot-card__time">{slot.time}</div>
           </div>
        ))}
      </div>

      {!isDelivery && mockCartInfo.is_curbside_pickup === "1" && (
        <div style={{ marginTop: '2rem' }}>
          <div 
            className={`toggle-card ${formState.isCurbsidePickup ? 'toggle-card--selected' : ''}`}
            onClick={() => updateForm('isCurbsidePickup', !formState.isCurbsidePickup)}
          >
            <div className="toggle-card__icon">🚗</div>
            <div className="toggle-card__content">
              <div className="toggle-card__title">Curbside Pickup</div>
              <div className="toggle-card__subtitle">We'll bring your order to your car</div>
            </div>
            <div className="toggle-card__toggle">
              <div className="switch" />
            </div>
          </div>

          {formState.isCurbsidePickup && (
            <div className="form-group">
              <label>Vehicle Description</label>
              <input 
                type="text" 
                placeholder="e.g. Blue Honda Civic"
                value={formState.curbsideComment}
                onChange={(e) => updateForm('curbsideComment', e.target.value)}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const Step2ContactAddress = ({
  formState,
  updateForm,
  mockAddresses,
  mockContactTypes,
  mockStoreDetails,
  mockUser,
  isDelivery,
}) => {
  return (
    <div className="wizard-card">
       <h2>Contact Information</h2>
       <div className="form-group">
         <label>Contact Method</label>
         <div style={{ display: 'grid', gridTemplateColumns: 'min-content 1fr', gap: '1rem', alignItems: 'start' }}>
             
             {/* New Segmented Toggle */}
             <div className="segmented-toggle">
               {mockContactTypes.map(t => (
                 <div
                   key={t.value}
                   className={`segmented-toggle__option ${formState.contactType === t.value ? 'segmented-toggle__option--selected' : ''}`}
                   onClick={() => updateForm('contactType', t.value)}
                 >
                   {t.value === 'call_me' && '📞'}
                   {t.value === 'text_me' && '💬'}
                   {t.label}
                 </div>
               ))}
             </div>
             
             <div style={{ flex: 1 }}>
                <input 
                  type="tel"
                  className="input-height-match" 
                  placeholder="(555) 000-0000"
                  value={formState.contactNumber}
                  onChange={(e) => updateForm('contactNumber', e.target.value)}
                  style={{ width: '100%' }}
                />
                <p className="error-msg" style={{ display: formState.contactNumber.length < 10 && formState.contactNumber.length > 0 ? 'block' : 'none' }}>
                  Please enter a valid 10-digit number
                </p>
             </div>
         </div>
       </div>

       <div className="form-group">
         <label>Order Notes (Optional)</label>
         <textarea
           placeholder="Special instructions..."
           value={formState.orderNotes}
           onChange={(e) => updateForm('orderNotes', e.target.value)}
           rows={3}
         />
       </div>

       {isDelivery ? (
         <>
           <h2 style={{ marginTop: '2rem' }}>Delivery Address</h2>
           <div className="option-grid vertical">
             {mockAddresses.shipping.map(addr => (
               <div
                 key={addr.id}
                 className={`option-card ${formState.selectedDeliveryAddress === addr.id ? 'option-card--selected' : ''}`}
                 onClick={() => updateForm('selectedDeliveryAddress', addr.id)}
               >
                 <div className="check-mark-circle">✓</div>
                 <div className="card-title">{addr.first_name} {addr.last_name}</div>
                 <div className="card-subtitle">
                   {addr.address_1} {addr.address_2}<br/>
                   {addr.city}, {addr.state} {addr.zip_code}
                 </div>
               </div>
             ))}
           </div>
           <button type="button" style={{ 
             background: 'none', 
             border: '1px dashed var(--cw2-primary)', 
             color: 'var(--cw2-primary)',
             padding: '0.75rem 1rem',
             borderRadius: '8px',
             cursor: 'pointer',
             width: '100%'
           }}>+ Add New Address</button>
         </>
       ) : (
         <>
           <h2 style={{ marginTop: '2rem' }}>Pickup Location</h2>
           <div className="option-card option-card--selected" style={{ cursor: 'default' }}>
             <div className="check-mark-circle">✓</div>
             <div className="card-title">{mockStoreDetails.ClientStoreName}</div>
             <div className="card-subtitle">
               {mockStoreDetails.store_address}<br/>
               {mockStoreDetails.store_city}, {mockStoreDetails.store_state} {mockStoreDetails.store_zip}
             </div>
           </div>
         </>
       )}
    </div>
  );
};

const Step3ReviewPayment = ({
  formState,
  updateForm,
  mockStoreDetails,
  mockAddresses,
  mockCartInfo,
  paymentMethods,
  grandTotal,
}) => {
  return (
    <div className="wizard-card">
      <h2>Order Options</h2>
      
      {mockStoreDetails.is_approved_tax_exempt === "1" && (
        <div 
          className={`toggle-card ${formState.isTaxExempt ? 'toggle-card--selected' : ''}`}
          onClick={() => updateForm('isTaxExempt', !formState.isTaxExempt)}
        >
          <div className="toggle-card__icon">🧾</div>
          <div className="toggle-card__content">
            <div className="toggle-card__title">Tax Exemption</div>
            <div className="toggle-card__subtitle">Apply tax exemption to this order</div>
          </div>
          <div className="toggle-card__toggle">
             <div className="switch" />
          </div>
        </div>
      )}

      {mockCartInfo.age_restricted_notes && (
         <div 
          className={`toggle-card toggle-card--warning ${formState.ageRestrictionAccepted ? 'toggle-card--selected' : ''}`}
          onClick={() => updateForm('ageRestrictionAccepted', !formState.ageRestrictionAccepted)}
        >
          <div className="toggle-card__icon" style={{ background: '#FFFBEB', color: '#F59E0B' }}>🔞</div>
          <div className="toggle-card__content">
            <div className="toggle-card__title">Age Verification</div>
            <div className="toggle-card__subtitle">I confirm I am 21+ years old</div>
          </div>
          <div className="toggle-card__toggle">
             <div className="switch" />
          </div>
        </div>
      )}
      
      {mockStoreDetails.include_printed_receipt_with_order === "1" && (
        <div 
          className={`toggle-card ${formState.includePrintedReceipt ? 'toggle-card--selected' : ''}`}
          onClick={() => updateForm('includePrintedReceipt', !formState.includePrintedReceipt)}
        >
          <div className="toggle-card__icon">📄</div>
          <div className="toggle-card__content">
             <div className="toggle-card__title">Printed Receipt</div>
             <div className="toggle-card__subtitle">Include physical receipt</div>
          </div>
          <div className="toggle-card__toggle">
             <div className="switch" />
          </div>
        </div>
      )}

      <h2 style={{ marginTop: '2rem' }}>Payment Method</h2>
      
      <div className="ebt-note">
        <strong>EBT SNAP</strong>
        Items sold by weight that are paid using EBT SNAP will have 10% of the cost of the weighted item added to the order to cover weight variances. This amount will be refunded if it is over the final amount provided when the order is fulfilled.
      </div>
      
      <div className="option-grid">
        {paymentMethods.map(method => (
          <div
            key={method.value}
            className={`option-card ${formState.selectedPaymentMethod === method.value ? 'option-card--selected' : ''}`}
            onClick={() => updateForm('selectedPaymentMethod', method.value)}
          >
             <div className="check-mark-circle">✓</div>
             <div className="card-icon">
               {method.value === 'cc' ? '💳' : method.value === 'cod' ? '💵' : method.value === 'ebt' ? '🪪' : '🏢'}
             </div>
             <div className="card-title">{method.label}</div>
             {method.balance && <div className="card-subtitle">${method.balance} Available</div>}
          </div>
        ))}
      </div>

      {formState.selectedPaymentMethod === 'cc' && (
        <div style={{ marginTop: '1rem', padding: '1.5rem', background: 'var(--cw2-bg-input)', borderRadius: '12px' }}>
          <h3>Billing Address</h3>
          <div className="option-grid vertical" style={{ marginTop: '1rem' }}>
             {mockAddresses.billing.map(addr => (
               <div
                 key={addr.id}
                 className={`option-card ${formState.selectedBillingAddress === addr.id ? 'option-card--selected' : ''}`}
                 onClick={() => updateForm('selectedBillingAddress', addr.id)}
               >
                 <div className="check-mark-circle">✓</div>
                 <div className="card-title">{addr.first_name} {addr.last_name}</div>
                 <div className="card-subtitle">
                   {addr.address_1}<br/>
                   {addr.zip_code}
                 </div>
               </div>
             ))}
          </div>
        </div>
      )}

    </div>
  );
};

const OrderSummary = ({ priceList, mockCartInfo, mockCoupons, selectedCoupons, onCouponToggle }) => {
  return (
    <div className="order-summary">
      <div className="order-summary__header">
        <h3>Order Summary</h3>
        <a href="#">Edit Cart</a>
      </div>
      <div className="order-summary__content">
        {priceList.map(item => (
          <div 
            key={item.key_name} 
            className={`summary-row ${item.key_name === 'grand_total' ? 'summary-row--total' : ''} ${item.amount.startsWith('-') ? 'summary-row--highlight' : ''}`}
          >
             <span>{item.title}</span>
             <span>${item.amount.replace('-', '-$')}</span>
          </div>
        ))}
        
        {/* Available Coupons */}
        <div className="coupons-section">
          <h4>Applied Coupons</h4>
          {mockCoupons.map(coupon => (
            <div key={coupon.coupon_value} className="coupon-item">
              <input 
                type="checkbox" 
                id={`coupon-${coupon.coupon_value}`}
                checked={selectedCoupons.includes(coupon.coupon_value)}
                onChange={() => onCouponToggle(coupon.coupon_value)}
              />
              <label htmlFor={`coupon-${coupon.coupon_value}`}>
                <strong>{coupon.coupon_title}</strong>
              </label>
            </div>
          ))}
        </div>

        {/* Authorization Note */}
        <div className="info-note">
           <span className="icon">ℹ️</span>
           <span>{mockCartInfo.authorized_notes}</span>
        </div>
      </div>
    </div>
  )
}

export default CheckoutWizardMockup2;
